import { useMemo, useRef, useState } from 'react'
import { useFiscalYear } from '@/lib/fiscalYear'
import {
  matchStatementLines,
  sumDollars,
  type CreditCardStatement,
  type ReconciliationPeriod,
  type StatementLine,
  type Transaction,
} from '@budgetmonkey/shared'
import { Badge, Button, Card, EmptyState, ErrorNote, Money } from '@/components/ui'
import {
  TransactionForm,
  type TransactionFormValues,
} from '@/features/transactions/TransactionForm'
import {
  useCreateTransaction,
  useTransactions,
  useTransactionsByIds,
} from '@/features/transactions/hooks'
import { TransactionDetail } from '@/features/transactions/TransactionDetail'
import { useCodesById } from '@/features/budgetCodes/hooks'
import { convertHeicForUpload, uploadReceipt } from '@/features/receipts/api'
import { useLinkReceipt } from '@/features/receipts/hooks'
import { useCurrentUser } from '@/auth/AuthProvider'
import { formatDate } from '@/lib/dates'
import {
  useGeneratePacket,
  useIngestStatement,
  useLinkStatementLine,
  useDeletePeriod,
  useMarkSubmitted,
  usePacketUrl,
  usePeriods,
  useSetPeriodItems,
  useStatement,
} from './hooks'
import { loadStateOf, LoadingOrOffline } from '@/lib/loadState'
import { humanErrorMessage } from '@/lib/errorMessage'

/**
 * Reconciliation — REQUIREMENTS.md §7.2, §8.1.
 *
 * One period per statement cycle, and the packet built from it. The period
 * owns the item order, because §8.1 ties log sheet item numbers to the receipt
 * pages printed behind them.
 *
 * The statement is the front door: it decides which charges appear, in what
 * order, and under which vendor spelling. Starting from the ledger and
 * ordering by date is the fallback §5.5 allows when no statement exists, and
 * it is tucked behind a disclosure rather than offered first — leading with it
 * misses the point of the feature, which is removing exactly that manual
 * reordering and retyping.
 */
export function ReconciliationScreen() {
  const fiscalYear = useFiscalYear()
  const periodsQuery = usePeriods()
  const { data: periods = [], error } = periodsQuery
  const state = loadStateOf(periodsQuery)
  const { data: transactions = [] } = useTransactions(fiscalYear)
  const ingest = useIngestStatement()
  const statementInput = useRef<HTMLInputElement>(null)
  const [openPeriodId, setOpenPeriodId] = useState<string>()
  const [dragging, setDragging] = useState(false)
  const [rejected, setRejected] = useState<string>()

  /**
   * Files reach here from the picker and from a drop, so the filtering lives
   * in one place. A file the extraction cannot read is refused by name rather
   * than uploaded and failed server-side a minute later.
   */
  function startIngest(files: readonly File[]) {
    if (files.length === 0) return

    const usable = files.filter(
      (file) => file.type.startsWith('image/') || file.type === 'application/pdf',
    )
    const refused = files.filter((file) => !usable.includes(file))

    setRejected(
      refused.length > 0
        ? `Not a statement page: ${refused.map((f) => f.name).join(', ')}. Upload images or a PDF.`
        : undefined,
    )
    if (usable.length === 0) return

    ingest.mutate(usable, { onSuccess: (r) => setOpenPeriodId(r.periodId) })
  }

  const openPeriod = periods.find((p) => p.id === openPeriodId)

  if (openPeriod !== undefined) {
    return (
      <PeriodDetail
        period={openPeriod}
        transactions={transactions}
        onClose={() => setOpenPeriodId(undefined)}
      />
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Reconciliation</h1>
        <p className="text-sm text-slate-500">
          One packet per statement cycle. Cycles run the 25th to the 24th.
        </p>
      </div>

      {error != null && <ErrorNote>{humanErrorMessage(error, "Couldn't load your packets.")}</ErrorNote>}

      {/* The statement is the front door. It decides which charges are on the
          packet, in what order, and under which vendor spelling (§5.5) — which
          is the manual reordering and retyping this is meant to remove. */}
      <Card className="space-y-4">
        <div>
          <h2 className="font-medium text-slate-900">Start from your statement</h2>
          <p className="mt-1 text-sm text-slate-600">
            Photograph or scan every page of the paper statement. The charges on
            it become the packet, in the order the statement lists them, with
            the vendor names it prints.
          </p>
        </div>

        {/* A drop target as well as a button: the CSIU import screen takes
            dropped files, so dragging a statement here is the obvious thing to
            try — and a page with no drop handler swallows it silently. */}
        <div
          onDragOver={(event) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            startIngest(Array.from(event.dataTransfer.files))
          }}
          className={`rounded-lg border-2 border-dashed px-4 py-6 text-center transition ${
            dragging ? 'border-slate-900 bg-slate-50' : 'border-slate-300'
          }`}
        >
          <Button
            variant="primary"
            onClick={() => statementInput.current?.click()}
            disabled={ingest.isPending}
          >
            {ingest.isPending
              ? ingest.progress !== undefined
                ? `Uploading page ${ingest.progress.done + 1} of ${ingest.progress.total}…`
                : 'Reading the statement…'
              : 'Upload statement'}
          </Button>
          <input
            ref={statementInput}
            type="file"
            multiple
            accept="image/*,application/pdf"
            className="sr-only"
            onChange={(event) => {
              startIngest(Array.from(event.target.files ?? []))
              event.target.value = ''
            }}
          />
          <p className="mt-2 text-xs text-slate-500">
            Or drop the pages here — images or a PDF, several at once.
          </p>
          {ingest.isPending && ingest.progress === undefined && (
            <p className="mt-2 text-sm text-slate-500" role="status">
              This takes up to a minute for a multi-page statement.
            </p>
          )}
        </div>

        {rejected !== undefined && <ErrorNote>{rejected}</ErrorNote>}
        {ingest.error !== null && <ErrorNote>{ingest.error.message}</ErrorNote>}

        {/* Said plainly, because a packet that quietly included Amazon or PO
            charges would ask the business office to sign for spending that
            reconciles through CSIU instead (§5.3). */}
        <p className="rounded-lg bg-slate-100 px-4 py-3 text-sm text-slate-600">
          Only P-Card charges go on a packet. Amazon, purchase orders and
          reimbursements are confirmed by a CSIU import instead, so they're left
          off.
        </p>
      </Card>


      {state !== 'ready' ? (
        <LoadingOrOffline state={state} what="Your packets" />
      ) : periods.length === 0 ? (
        <EmptyState title="No packets yet">
          <p>Upload a statement above to start one.</p>
        </EmptyState>
      ) : (
        <ul className="space-y-2">
          {periods.map((period) => (
            <li key={period.id}>
              <button
                type="button"
                onClick={() => setOpenPeriodId(period.id)}
                className="flex w-full flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-left transition hover:bg-slate-50"
              >
                <span>
                  <span className="block font-medium text-slate-900">
                    {formatDate(period.cycleEnd)} cycle
                  </span>
                  <span className="block text-xs text-slate-500">
                    {period.orderedTransactionIds.length} item
                    {period.orderedTransactionIds.length === 1 ? '' : 's'} ·{' '}
                    {formatDate(period.cycleStart)} – {formatDate(period.cycleEnd)}
                  </span>
                </span>
                <StatusBadge status={period.status} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: ReconciliationPeriod['status'] }) {
  if (status === 'submitted') return <Badge tone="ok">Submitted</Badge>
  if (status === 'packet_generated') return <Badge>Packet ready</Badge>
  // A statement has been read but the packet has not been built yet. Labelling
  // that "Open" hides the one state where there is something specific to do.
  if (status === 'matched') return <Badge>Statement read</Badge>
  return <Badge tone="warn">Open</Badge>
}

/**
 * What the statement said, and what is still missing from the ledger.
 *
 * Every charge the statement prints has to end up on the packet, so this lists
 * all of them rather than only the ones that matched. A line with no capture
 * behind it is the actual work item — §8.1 would otherwise print it as a
 * tombstone, which is a confession, not a packet.
 *
 * Capturing from a line is deliberately the whole transaction form and not a
 * quick two-field affair: a charge still needs a budget code, and guessing one
 * puts the error somewhere much more expensive than this screen.
 */
function StatementLines({
  statement,
  period,
  transactions,
  onOpenTransaction,
}: {
  statement: CreditCardStatement
  period: ReconciliationPeriod
  transactions: readonly Transaction[]
  onOpenTransaction: (transactionId: string) => void
}) {
  const user = useCurrentUser()
  const create = useCreateTransaction()
  const link = useLinkStatementLine(statement.id!, period.id!)
  const linkReceipt = useLinkReceipt()
  const [capturing, setCapturing] = useState<StatementLine>()
  const [receiptFiles, setReceiptFiles] = useState<File[]>([])
  const [receiptError, setReceiptError] = useState<string>()
  const [attaching, setAttaching] = useState(false)

  const byId = useMemo(
    () => new Map(transactions.map((t) => [t.id ?? '', t])),
    [transactions],
  )

  const missing = statement.lines.filter((line) => line.matchedTransactionId === null)
  const readTotal = sumDollars(statement.lines.map((line) => line.amount))

  /**
   * Charges captured somewhere else — the receipts screen, manual entry —
   * still belong to their statement line, and until something says so the line
   * reads as uncaptured and the packet leaves it off.
   *
   * The same matcher the ingest uses runs again here over what is in the
   * ledger now, against transactions no line has already claimed. It suggests
   * rather than links: a wrong automatic link is a wrong packet, and the
   * confirmation is one click.
   */
  const suggestions = useMemo(() => {
    if (missing.length === 0) {
      return new Map<number, { transaction: Transaction; sure: boolean }>()
    }

    const claimed = new Set(
      statement.lines
        .map((line) => line.matchedTransactionId)
        .filter((id): id is string => id !== null),
    )
    const available = transactions
      .filter(
        (t) =>
          t.id !== undefined &&
          t.source === 'pcard' &&
          t.status !== 'void' &&
          !claimed.has(t.id),
      )
      .map((t) => ({
        id: t.id!,
        vendor: t.vendor,
        amount: t.amount,
        transactionDate: t.transactionDate,
      }))

    const found = new Map<number, { transaction: Transaction; sure: boolean }>()
    for (const match of matchStatementLines(missing, available)) {
      if (match.transactionId === undefined) continue

      // `amount_only` is offered too, flagged as needing a look. A flight
      // booked as "Southwest Airlines" against a statement's own rendering of
      // it agrees on the amount and the day but not on the tokens, and
      // discarding those was leaving real captures looking uncaptured.
      if (match.kind !== 'matched' && match.kind !== 'amount_only') continue

      const transaction = byId.get(match.transactionId)
      if (transaction !== undefined) {
        found.set(match.line.lineIndex, {
          transaction,
          sure: match.kind === 'matched',
        })
      }
    }
    return found
  }, [missing, statement.lines, transactions, byId])

  async function capture(values: TransactionFormValues) {
    if (capturing === undefined) return

    const { id: transactionId } = await create.mutateAsync(values)
    await link.mutateAsync({ lineIndex: capturing.lineIndex, transactionId })

    // Receipts attach after the transaction exists, and a failure is reported
    // without discarding the capture: the charge is on the packet either way,
    // and re-entering it to retry a photo would be worse.
    //
    // Sequential, so they attach in the order they were chosen — that is the
    // order they print behind the log sheet line, and for a two-page bill the
    // order is the difference between readable and not.
    if (receiptFiles.length > 0) {
      setAttaching(true)
      const failed: string[] = []

      for (const file of receiptFiles) {
        try {
          const prepared = await convertHeicForUpload(file)
          const handle = uploadReceipt(user.uid, prepared, 'desktop_upload')
          await handle.done
          await linkReceipt.mutateAsync({ receiptId: handle.receiptId, transactionId })
        } catch (cause) {
          // One bad file must not cost the others — the same rule the packet
          // assembler follows.
          failed.push(
            `${file.name} (${cause instanceof Error ? cause.message : String(cause)})`,
          )
        }
      }

      setAttaching(false)
      setReceiptError(
        failed.length === 0
          ? undefined
          : `The charge was captured, but ${failed.length} receipt${
              failed.length === 1 ? '' : 's'
            } didn't attach: ${failed.join('; ')}. Add ${
              failed.length === 1 ? 'it' : 'them'
            } from the Receipts screen.`,
      )
    }

    setCapturing(undefined)
    setReceiptFiles([])
  }

  if (capturing !== undefined) {
    return (
      <Card className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Capture line {capturing.lineIndex}</h2>
          <p className="text-sm text-slate-600">
            {/* Printed verbatim: this is the spelling the business office
                reconciles against, so an improved one is wrong (§8.1). */}
            <span className="font-mono">{capturing.merchantAsPrinted}</span> ·{' '}
            <Money value={capturing.amount} /> · {formatDate(capturing.transactionDate)}
          </p>
        </div>
        <TransactionForm
          submitLabel="Add to the packet"
          prefill={{
            vendor: capturing.merchantAsPrinted,
            amount: capturing.amount,
            transactionDate: capturing.transactionDate,
            source: 'pcard',
          }}
          onSubmit={(values) => void capture(values)}
          onCancel={() => {
            setCapturing(undefined)
            setReceiptFiles([])
          }}
          pending={create.isPending || link.isPending || attaching}
          error={create.error?.message ?? link.error?.message}
        >
          {/* The receipt belongs here. Every P-Card charge needs one, and
              sending Matt to a second screen to attach it is how a packet
              reaches the business office with a line and no paper behind it. */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <label className="block text-sm font-medium" htmlFor="capture-receipt">
              Receipt
            </label>
            {/* The browser's default file control renders as bare text, which
                reads as a label rather than something to press. The `file:`
                variants style the button half of it. */}
            {/* `multiple`, because a single charge often needs more than one
                page of evidence — an itemised bill and the card slip, a
                two-sided form photographed twice. They all attach to the one
                charge and print behind its log sheet line in order. */}
            <input
              id="capture-receipt"
              type="file"
              multiple
              accept="image/*,application/pdf"
              className="mt-2 block w-full text-sm text-slate-500 file:mr-3 file:min-h-9 file:cursor-pointer file:rounded-lg file:border file:border-slate-300 file:bg-white file:px-3 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-50"
              onChange={(event) => setReceiptFiles(Array.from(event.target.files ?? []))}
            />
            <p className="mt-1 text-xs text-slate-500">
              {receiptFiles.length > 0
                ? `${receiptFiles.map((f) => f.name).join(', ')} — ${receiptFiles.length} file${receiptFiles.length === 1 ? '' : 's'} for this charge.`
                : 'Optional here, and you can pick more than one. You can also add them later from Receipts.'}
            </p>
          </div>
        </TransactionForm>
      </Card>
    )
  }

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          From the statement
        </h2>
        <p className="text-sm text-slate-600">
          {statement.lines.length} charge{statement.lines.length === 1 ? '' : 's'} read ·{' '}
          <Money value={readTotal} />
          {statement.statementTotal !== null &&
            Math.abs(statement.statementTotal - readTotal) >= 0.01 && (
              <span className="text-amber-700">
                {' '}
                · statement says <Money value={statement.statementTotal} />
              </span>
            )}
        </p>
      </div>

      {receiptError !== undefined && <ErrorNote>{receiptError}</ErrorNote>}

      {missing.length > 0 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {missing.length - suggestions.size > 0 && (
            <>
              {missing.length - suggestions.size} of these {statement.lines.length}{' '}
              charges {missing.length - suggestions.size === 1 ? 'is' : 'are'} not in
              your ledger yet. Capture them and they take their place on the packet
              automatically, in statement order.
            </>
          )}
          {suggestions.size > 0 && (
            <>
              {missing.length - suggestions.size > 0 && ' '}
              {suggestions.size} already{' '}
              {suggestions.size === 1 ? 'matches a charge' : 'match charges'} you
              captured elsewhere — check the name and amount, then link{' '}
              {suggestions.size === 1 ? 'it' : 'them'}.
            </>
          )}
        </p>
      )}

      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
        {statement.lines.map((line) => {
          const matched =
            line.matchedTransactionId !== null
              ? byId.get(line.matchedTransactionId)
              : undefined
          const suggested = suggestions.get(line.lineIndex)

          return (
            // The merchant string wraps rather than truncating: it is the one
            // piece of text on this screen that has to be checked character by
            // character against the paper statement, and "SQ *SUBP…" cannot be.
            // On a phone the amount and action drop to their own line to make
            // room for it.
            <li
              key={line.lineIndex}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3"
            >
              <span className="w-6 shrink-0 text-sm text-slate-400 tabular">
                {line.lineIndex}
              </span>
              <span className="min-w-40 flex-1">
                <span className="block break-words font-mono text-sm">
                  {line.merchantAsPrinted}
                </span>
                <span className="block text-xs text-slate-500">
                  {formatDate(line.transactionDate)}
                  {matched !== undefined && ` · captured as ${matched.vendor}`}
                  {suggested !== undefined && (
                    <span className="text-amber-700">
                      {suggested.sure
                        ? ` · already in your ledger as ${suggested.transaction.vendor}`
                        : ` · same amount as ${suggested.transaction.vendor} — check this is the same charge`}
                    </span>
                  )}
                </span>
              </span>
              <span className="ml-9 flex basis-full items-center justify-end gap-3 sm:ml-0 sm:basis-auto">
                <Money value={line.amount} className="text-sm" />
                {line.matchedTransactionId !== null ? (
                  // The badge is the button. It says what the line's state is
                  // and opens the charge behind it — receipts included —
                  // without leaving the statement being worked through.
                  <Button
                    variant="ghost"
                    onClick={() => onOpenTransaction(line.matchedTransactionId!)}
                  >
                    <Badge tone="ok">On the packet</Badge>
                    <span className="ml-2 text-xs text-slate-500">Open</span>
                  </Button>
                ) : suggested !== undefined ? (
                  <Button
                    variant={suggested.sure ? 'primary' : undefined}
                    disabled={link.isPending}
                    onClick={() =>
                      link.mutate({
                        lineIndex: line.lineIndex,
                        transactionId: suggested.transaction.id!,
                      })
                    }
                  >
                    {suggested.sure ? 'Link' : 'Link anyway'}
                  </Button>
                ) : (
                  <Button onClick={() => setCapturing(line)}>Capture</Button>
                )}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/** One cycle: its items in packet order, and the packet itself. */
function PeriodDetail({
  period,
  transactions,
  onClose,
}: {
  period: ReconciliationPeriod
  transactions: readonly Transaction[]
  onClose: () => void
}) {
  const setItems = useSetPeriodItems()
  const generate = useGeneratePacket()
  const submit = useMarkSubmitted()
  const { data: packetUrl } = usePacketUrl(period.packetPdfUrl)
  const { data: xlsxUrl } = usePacketUrl(period.packetXlsxUrl)
  const { data: statement } = useStatement(period.statementId)
  const deletePacket = useDeletePeriod()
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [openTransactionId, setOpenTransactionId] = useState<string>()
  const fiscalYear = useFiscalYear()

  // Resolved across years, not just the one on screen: a charge moved to
  // last year still points at the code document it was filed against.
  const codesById = useCodesById()

  // The packet's own charges, fetched by id rather than taken from the year
  // being viewed. A cycle straddles the July rollover — the July statement
  // carries charges dated in June, which belong to the year that just ended —
  // so reading them out of one year's list drops them here while
  // `generatePacket`, which fetches by id, prints them. A packet screen that
  // disagrees with the packet is the one thing this screen must never do.
  const { data: periodTransactions = [] } = useTransactionsByIds(
    period.orderedTransactionIds,
  )

  const byId = useMemo(() => {
    const map = new Map(transactions.map((t) => [t.id ?? '', t]))
    // Last write wins, and the by-id read is the authoritative one.
    for (const t of periodTransactions) map.set(t.id ?? '', t)
    return map
  }, [transactions, periodTransactions])

  const items = period.orderedTransactionIds
    .map((id) => byId.get(id))
    .filter((t): t is Transaction => t !== undefined)

  const total = sumDollars(items.map((t) => t.amount))

  // Charges this packet carried that it has not confirmed. Non-empty only on a
  // packet submitted before submission confirmed anything, or one whose charges
  // were somehow left behind.
  const unconfirmedItems = items.filter(
    (t) => t.status !== 'void' && t.reconciledVia !== 'packet',
  )

  // A captured charge opens in place of the packet, not above it: the receipt
  // is what most often needs attention while working down a statement, and
  // sending Matt to the Transactions screen loses his place in the list. The
  // whole view swaps so there is one Back and one heading, rather than a
  // stale packet total sitting over the charge being edited.
  if (openTransactionId !== undefined) {
    return (
      <TransactionDetail
        transactionId={openTransactionId}
        codesById={codesById}
        fiscalYear={fiscalYear}
        onClose={() => setOpenTransactionId(undefined)}
      />
    )
  }

  function move(index: number, delta: number) {
    const ids = [...period.orderedTransactionIds]
    const target = index + delta
    if (target < 0 || target >= ids.length) return
    const [moved] = ids.splice(index, 1)
    if (moved !== undefined) ids.splice(target, 0, moved)
    setItems.mutate({ periodId: period.id!, ids })
  }

  function remove(index: number) {
    const ids = [...period.orderedTransactionIds]
    ids.splice(index, 1)
    setItems.mutate({ periodId: period.id!, ids })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={onClose} className="-ml-4">
          ← All packets
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {formatDate(period.cycleEnd)} cycle
        </h1>
      </div>

      <Card className="space-y-3">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Packet total</p>
            <Money value={total} className="text-2xl font-semibold" />
          </div>
          <p className="text-sm text-slate-600">
            {items.length} item{items.length === 1 ? '' : 's'} ·{' '}
            {Math.max(1, Math.ceil(items.length / 10))} page
            {items.length > 10 ? 's' : ''}
          </p>
        </div>

        <div className="flex flex-wrap gap-3 border-t border-slate-100 pt-3">
          <Button
            variant="primary"
            onClick={() => generate.mutate(period.id!)}
            disabled={generate.isPending || items.length === 0}
          >
            {generate.isPending ? 'Building…' : 'Generate packet'}
          </Button>

          {/* Both formats are the deliverable, not a PDF with a spreadsheet
              afterthought — §8.1, and Matt asked for both explicitly. */}
          {packetUrl !== undefined && (
            <a
              href={packetUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-11 items-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium hover:bg-slate-50"
            >
              Open the PDF
            </a>
          )}

          {xlsxUrl !== undefined && (
            <a
              href={xlsxUrl}
              className="inline-flex min-h-11 items-center rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium hover:bg-slate-50"
            >
              Download the Excel copy
            </a>
          )}

          {period.status !== 'submitted' && period.packetPdfUrl !== null && (
            <Button onClick={() => submit.mutate(period.id!)} disabled={submit.isPending}>
              Mark submitted
            </Button>
          )}

          {/* A packet submitted before submitting confirmed anything left its
              charges reading "Unconfirmed" with no way to put it right — the
              button that would have done it hides once the packet is in. This
              appears only while that mismatch exists and goes away once it is
              fixed, so it is a repair rather than a permanent control. */}
          {period.status === 'submitted' && unconfirmedItems.length > 0 && (
            <Button
              variant="primary"
              onClick={() => submit.mutate(period.id!)}
              disabled={submit.isPending}
            >
              {submit.isPending
                ? 'Confirming…'
                : `Confirm ${unconfirmedItems.length} charge${
                    unconfirmedItems.length === 1 ? '' : 's'
                  }`}
            </Button>
          )}

          {/* Two-step, because it is irreversible and sits beside buttons
              pressed routinely. The transactions survive — only the packet
              and its statement go. */}
          {confirmingDelete ? (
            <span className="flex items-center gap-2">
              <Button
                variant="danger"
                onClick={() => deletePacket.mutate(period.id!, { onSuccess: onClose })}
                disabled={deletePacket.isPending}
              >
                {deletePacket.isPending ? 'Deleting…' : 'Delete for good'}
              </Button>
              <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
                Keep it
              </Button>
            </span>
          ) : (
            <Button variant="ghost" onClick={() => setConfirmingDelete(true)}>
              Delete packet
            </Button>
          )}
        </div>

        {generate.error !== null && <ErrorNote>{generate.error.message}</ErrorNote>}
        {generate.data !== undefined && (
          <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
            Built {generate.data.pages} page{generate.data.pages === 1 ? '' : 's'},{' '}
            {generate.data.items} items, total{' '}
            <Money value={generate.data.grandTotal} />.
          </p>
        )}
      </Card>

      {statement != null && (
        <StatementLines
          statement={statement}
          period={period}
          transactions={transactions}
          onOpenTransaction={setOpenTransactionId}
        />
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Items, in packet order
        </h2>
        {/* Item number, order and the receipt printed behind it are the same
            list (§8.1), so moving a row here moves all three. */}
        <p className="text-xs text-slate-500">
          Item numbers follow this order. Once a statement is ingested it sets
          the order to match, which is what the business office reconciles
          against.
        </p>

        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
          {items.map((transaction, index) => (
            <li key={transaction.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <span className="w-6 text-sm text-slate-400 tabular">{index + 1}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {transaction.vendor}
                </span>
                <span className="block text-xs text-slate-500">
                  {formatDate(transaction.transactionDate)}
                  {transaction.description !== '' && ` · ${transaction.description}`}
                </span>
              </span>
              <Money value={transaction.amount} className="text-sm" />
              <span className="flex gap-1">
                <Button
                  variant="ghost"
                  onClick={() => move(index, -1)}
                  disabled={index === 0 || setItems.isPending}
                  aria-label="Move up"
                >
                  ↑
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => move(index, 1)}
                  disabled={index === items.length - 1 || setItems.isPending}
                  aria-label="Move down"
                >
                  ↓
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => remove(index)}
                  disabled={setItems.isPending}
                  aria-label="Remove from packet"
                >
                  ✕
                </Button>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
